import { apiConfig } from '../config/apiConfig';
import {
  provideEmptyNotifications, 
  provideEmptyNotificationCount,
  provideEmptyUsers,
  provideEmptySentNotifications
} from './api-fallback';

// Cache for API responses
const apiCache: Record<string, {
  data: any;
  timestamp: number;
  expiry: number;
}> = {};

// Configurable cache TTL in milliseconds (default: 5 seconds)
// This is used in the getUserData function
export const DEFAULT_CACHE_TTL = 5000;

// API helper functions
export const getApiBaseUrl = () => {
  const isProduction = process.env.NODE_ENV === 'production';
  // Use a relative URL for development to avoid CORS issues
  return isProduction 
    ? 'https://the-league-system-backend.onrender.com/api' 
    : '/api';
};

// Add this function to handle various user ID formats
const ensureCorrectIdFormat = (id: string | number | null): string => {
  if (!id) return "";
  
  // Convert to string if it's a number
  const idStr = String(id);
  
  // Check if it looks like a UUID
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  if (uuidRegex.test(idStr)) {
    return idStr;
  }
  
  // If it's just a number, return as is
  return idStr;
};

// Global tracking of fetch attempts and errors
let fetchFailureCount = 0;
let lastFetchError = 0;
const MAX_FETCH_FAILURES = 5;
const FETCH_RECOVERY_TIME = 60000; // 1 minute cooldown period

// Modify fetchWithAuth to add resource error handling
export const fetchWithAuth = async (
  endpoint: string, 
  options: RequestInit = {},
  cacheTTL: number = 0,  // Default to no caching
  silent: boolean = false
) => {
  try {
    // Check if we're in a failure state
    const now = Date.now();
    if (
      fetchFailureCount >= MAX_FETCH_FAILURES && 
      (now - lastFetchError) < FETCH_RECOVERY_TIME
    ) {
      if (!silent) {
        console.log(`Skipping fetch to ${endpoint} during recovery period`);
      }
      throw new Error('API in cooldown due to resource errors');
    }
    
    // Get auth token once at the beginning
    const token = localStorage.getItem('token');
    
    if (!token) {
      throw new Error('Authentication token not found');
    }
    
    // Check cache first if cacheTTL is set
    if (cacheTTL > 0) {
      const cacheKey = `${endpoint}:${JSON.stringify(options)}`;
      const cachedItem = apiCache[cacheKey];
      
      if (cachedItem && (Date.now() - cachedItem.timestamp) < cachedItem.expiry) {
        // Return cached data if it's not expired
        return cachedItem.data;
      }
    }
    
    // Build request URL
    let url = endpoint;
    const isProduction = process.env.NODE_ENV === 'production';
    const baseApiUrl = isProduction 
      ? 'https://the-league-system-backend.onrender.com' 
      : '';
    
    if (!endpoint.startsWith('http')) {
      // Handle different API endpoint formats consistently
      if (endpoint.startsWith('/api/')) {
        // Endpoint already has /api/ prefix
        url = `${baseApiUrl}${endpoint}`;
      } else if (endpoint.startsWith('/')) {
        // Endpoint starts with / but not /api/
        url = `${baseApiUrl}/api${endpoint}`;
      } else {
        // Endpoint doesn't start with /
        url = `${baseApiUrl}/api/${endpoint}`;
      }
      
      // Log the final URL if not in silent mode
      if (!silent) {
        console.log(`Constructed API URL: ${url}`);
      }
    }
    
    // Special handling for admin dashboard stats to avoid userId parameter
    const skipUserIdEndpoints = [
      '/api/admin/dashboard-stats', 
      '/api/dashboard/stats'
    ];
    
    const shouldAddUserId = (endpoint.includes('/history') || 
                           endpoint.includes('/stats') || 
                           endpoint.includes('/performance')) &&
                          !endpoint.includes('userId=') && 
                          !skipUserIdEndpoints.some(e => endpoint.includes(e));
    
    // Add userId as a query parameter for applicable endpoints
    if (shouldAddUserId) {
      const userId = localStorage.getItem('userId');
      if (userId) {
        // Use proper ID format and ensure URL safety
        const formattedId = ensureCorrectIdFormat(userId);
        const safeUserId = encodeURIComponent(formattedId);
        url += (url.includes('?') ? '&' : '?') + `userId=${safeUserId}`;
        if (!silent) {
          console.log(`Added userId=${safeUserId} to URL: ${url}`);
        }
      } else if (!silent) {
        console.warn('No userId found in localStorage for history request');
      }
    }
    
    // Log the request if not silent
    if (!silent) {
      console.log(`Making API request to: ${url} ${options.method || 'GET'}`);
      
      // Log the request body if it exists and not silent
      if (options.body && typeof options.body === 'string') {
        try {
          const parsedBody = JSON.parse(options.body);
          console.log('Request body:', parsedBody);
        } catch (e) {
          // If it's not valid JSON, just log as is
          console.log('Request body (raw):', options.body);
        }
      }
    }

    // Use AbortController to set timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
    
    // Ensure proper headers are set
    const headers: Record<string, string> = {
      ...options.headers as Record<string, string>
    };
    
    // Only set Content-Type if not already set and if there's a body
    if (!headers['Content-Type'] && options.body) {
      headers['Content-Type'] = 'application/json';
    }
    
    // Always set Authorization header
    headers['Authorization'] = `Token ${token}`;
    
    // Make request with timeout
    const response = await fetch(url, {
      ...options,
      headers,
      signal: controller.signal
    });
    
    // Clear the timeout
    clearTimeout(timeoutId);

    // Check for non-JSON responses
    const contentType = response.headers.get('content-type');
    const isJson = contentType && contentType.includes('application/json');

    // Handle non-OK responses
    if (!response.ok) {
      let errorMessage;
      let errorData = null;
      
      if (isJson) {
        try {
          errorData = await response.json();
          if (!silent) {
            console.error('Error response data:', errorData);
          }
          errorMessage = errorData.detail || errorData.error || errorData.message || `Server returned ${response.status}: ${response.statusText}`;
        } catch (e) {
          errorMessage = `Server returned ${response.status}: (while accessing ${endpoint})`;
        }
      } else {
        try {
          // Try to get text response for better error messages
          const textResponse = await response.text();
          errorMessage = `Server returned ${response.status}: ${textResponse || response.statusText} (while accessing ${endpoint})`;
        } catch (e) {
          // Fallback if text extraction fails
          errorMessage = `Server returned ${response.status}: (while accessing ${endpoint})`;
        }
      }
      
      const error = new Error(errorMessage);
      (error as any).status = response.status;
      (error as any).statusText = response.statusText;
      (error as any).errorData = errorData;
      
      throw error;
    }
    
    // Parse response
    let data;
    if (isJson) {
      data = await response.json();
    } else {
      // For non-JSON responses, return text or null
      const text = await response.text();
      data = text || null;
      // Log warning about non-JSON response
      if (!silent) {
        console.warn(`Warning: Endpoint ${endpoint} returned non-JSON response`);
      }
    }

    // Store in cache if cacheTTL is set
    if (cacheTTL > 0) {
      const cacheKey = `${endpoint}:${JSON.stringify(options)}`;
      apiCache[cacheKey] = {
        data,
        timestamp: Date.now(),
        expiry: cacheTTL
      };
    }
    
    // Reset failure count on success
    fetchFailureCount = 0;
    
    return data;
  } catch (error: any) {
    // Check if this is a resource error or an abort error
    if (
      error.name === 'AbortError' ||
      (error.message && error.message.includes('ERR_INSUFFICIENT_RESOURCES'))
    ) {
      // Increment failure count and record time
      fetchFailureCount++;
      lastFetchError = Date.now();
      
      console.error(`Resource error for ${endpoint} (${fetchFailureCount}/${MAX_FETCH_FAILURES})`);
      
      // If we've hit the threshold, show a user-friendly message
      if (fetchFailureCount >= MAX_FETCH_FAILURES && !silent) {
        console.error('Too many resource errors, entering cooldown period');
      }
    }
    
    if (!silent) {
      console.error(`API Error for ${endpoint}:`, error);
    }
    throw error;
  }
};

/**
 * Fetch data from API silently without throwing errors
 * Used for non-critical operations like notifications
 */
export const fetchWithAuthSilent = async (endpoint: string, options: RequestInit = {}): Promise<any> => {
  try {
    return await fetchWithAuth(endpoint, options, 0, true);
  } catch (err) {
    console.debug(`Silent API error for ${endpoint}:`, err);
    
    // Return fallback data based on endpoint
    if (endpoint.includes('notifications/count')) {
      return provideEmptyNotificationCount();
    } else if (endpoint.includes('notifications')) {
      return provideEmptyNotifications();
    } else if (endpoint.includes('users')) {
      return provideEmptyUsers();
    } else if (endpoint.includes('admin/notifications')) {
      return provideEmptySentNotifications();
    }
    
    // Default fallback
    return null;
  }
};

/**
 * Checks if the current session is valid by calling the heartbeat endpoint.
 * If the session is invalid (401/403), clears storage and redirects to /session-expired.
 * Returns true if valid, false if not.
 */
export const checkSessionValidity = async (silent = true): Promise<boolean> => {
  const token = localStorage.getItem('token');
  if (!token) {
    if (typeof window !== 'undefined' && window.showSessionExpiredModal) window.showSessionExpiredModal();
    return false;
  }
  try {
    await fetchWithAuth('/api/auth/heartbeat/', { method: 'POST' }, 0, silent);
    return true;
  } catch (error: any) {
    if (error && (error.status === 401 || error.status === 403)) {
      localStorage.clear();
      if (typeof window !== 'undefined' && window.showSessionExpiredModal) window.showSessionExpiredModal();
      return false;
    }
    // Other errors: do not redirect, just log
    if (!silent) {
      console.error('Session check error:', error);
    }
    return false;
  }
};

// Add a utility function to invalidate cache for specific endpoints
export const invalidateCache = (endpoint: string) => {
  const url = `${apiConfig.baseURL}${endpoint.startsWith('/') ? endpoint : '/' + endpoint}`;
  delete apiCache[url];
  console.debug(`Cache invalidated for ${endpoint}`);
};

// Add a utility to clear all cache
export const clearCache = () => {
  Object.keys(apiCache).forEach(key => delete apiCache[key]);
  console.debug("API cache cleared");
};

// Add a fallback mechanism for user data
export const getUserData = async (username: string, silent = false): Promise<any> => {
  // Try each endpoint in order until one works
  const endpoints = [
    `/api/users/${username}/`, 
    `/api/users/me/`,
    `/users/${username}/`,
    `/users/profile/`
  ];
  
  for (const endpoint of endpoints) {
    try {
      // Use the cache TTL constant here
      return await fetchWithAuth(endpoint, {}, DEFAULT_CACHE_TTL, silent);
    } catch (err) {
      // Continue to next endpoint
    }
  }
  
  // If all endpoints fail, return a default user object
  return {
    username: username,
    avatar_url: localStorage.getItem("avatar_url") || "/avatars/avatar1.png",
    email: localStorage.getItem("email") || "",
    first_name: localStorage.getItem("first_name") || "",
    last_name: localStorage.getItem("last_name") || ""
  };
};

// Add this function to your api.ts file

export interface ApiError extends Error {
  status?: number;
  statusText?: string;
  details?: any;
}

export const handleApiError = (error: unknown): ApiError => {
  if (error instanceof Error) {
    const apiError: ApiError = error;
    
    // Extract HTTP status code from error message if available
    const statusMatch = error.message.match(/(\d{3})/);
    if (statusMatch) {
      apiError.status = parseInt(statusMatch[1], 10);
    }
    
    return apiError;
  }
  
  // If it's not an Error object, create one
  return new Error(typeof error === 'string' ? error : 'An unknown error occurred');
};

